/**
 * Which failures are the server's answer, and which are just the network.
 *
 * The distinction decides whether a punch is retried forever or shown to the
 * worker. Getting it wrong in one direction spams the API with a request that
 * can never succeed; in the other it silently drops a punch, which is a
 * payroll defect (I-7).
 */
import type { ErrorCode } from '@saniplus/contracts';

/**
 * A decision the server has made about this punch. Re-sending the identical
 * payload cannot change any of them, so the punch moves to `failedPunches`
 * and the worker is told.
 */
export const PUNCH_REFUSAL_CODES: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
  'OUTSIDE_PUNCH_WINDOW',
  'NO_SHIFT_SCHEDULED',
  'CONTRACT_INACTIVE',
  'STALE_OPEN_PUNCH',
  'INVALID_CAPTURE_TIME',
  'NO_OPEN_PUNCH',
  'INVALID_PUNCH_OUT_TIME',
  'OUTSIDE_GEOFENCE',
  'DEVICE_NOT_REGISTERED',
  'FORBIDDEN',
  'NOT_FOUND',
  // A payload the frozen schema refuses will be refused identically forever;
  // it is a client bug, and hiding it behind an infinite retry hides the punch.
  'VALIDATION_FAILED',
  // Phase 5: the shift has pending blocking tasks and this OUT carried no
  // override reason. Resending the same payload is refused identically
  // forever — the worker must see it, not have it retried silently.
  'TASKS_INCOMPLETE',
]);

export function isPunchRefusal(code: ErrorCode): boolean {
  return PUNCH_REFUSAL_CODES.has(code);
}

/**
 * Everything else — offline, 5xx, 429, an expired session waiting on a
 * refresh — is a transport problem. The punch stays queued and the scheduler
 * owns the retry.
 */
export function isRetryable(code: ErrorCode): boolean {
  return !PUNCH_REFUSAL_CODES.has(code);
}

/**
 * The i18n key for a refusal, in this feature's namespace. Workers never see a
 * code (`TRACK-D` "No error codes shown to workers"); they see the sentence
 * this key resolves to, and every code in the set above has one.
 */
export function refusalMessageKey(code: ErrorCode): string {
  return PUNCH_REFUSAL_CODES.has(code) ? `offline:refusal.${code}` : 'offline:refusal.default';
}

/** The 60/60 bounds an `OUTSIDE_PUNCH_WINDOW` refusal was judged against. */
export interface RefusalWindow {
  readonly windowOpensAt: string;
  readonly windowClosesAt: string;
}

function isIsoInstant(value: unknown): value is string {
  return typeof value === 'string' && !Number.isNaN(Date.parse(value));
}

/**
 * The bounds the *server* used, read off the refusal's own `error.details`.
 *
 * `punch.service.ts` puts `windowOpensAt`/`windowClosesAt` there precisely so
 * the PWA can say « vous pourrez pointer à 21 h 00 » instead of « refusé ».
 * W8 must prefer these over `/me/context`: a refusal is retained until the
 * worker acknowledges it (I-7), and by then the live context is describing the
 * next shift's window, or none at all.
 *
 * `null` when the refusal carries no usable bounds — the caller falls back to
 * the live context rather than rendering an invented time.
 */
export function refusalWindow(details: unknown): RefusalWindow | null {
  if (typeof details !== 'object' || details === null) return null;
  const { windowOpensAt, windowClosesAt } = details as Record<string, unknown>;
  if (!isIsoInstant(windowOpensAt) || !isIsoInstant(windowClosesAt)) return null;
  return { windowOpensAt, windowClosesAt };
}
